import { useState } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  LinearProgress,
  useTheme
} from '@mui/material';
import {
  Construction,
  Engineering,
  Handyman,
  CheckCircle,
  Warning,
  Error,
  Add,
  Edit,
  Delete,
  CalendarToday
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const Equipment = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [equipment, setEquipment] = useState([
    {
      id: 1,
      name: 'Caterpillar Excavator',
      type: 'heavy',
      status: 'operational',
      location: 'Site A - Foundation Zone',
      lastMaintenance: '2024-03-01',
      nextMaintenance: '2024-04-01',
      hoursUsed: 245
    },
    {
      id: 2,
      name: 'Concrete Mixer Truck',
      type: 'vehicle',
      status: 'maintenance',
      location: 'Main Yard',
      lastMaintenance: '2024-02-15',
      nextMaintenance: '2024-03-20',
      hoursUsed: 180
    },
    {
      id: 3,
      name: 'Jack Hammer',
      type: 'hand',
      status: 'out-of-service',
      location: 'Tool Storage B',
      lastMaintenance: '2023-12-10',
      nextMaintenance: '2024-01-10',
      hoursUsed: 420
    }
  ]);

  const statusConfig = {
    operational: { color: 'success', icon: <CheckCircle /> },
    maintenance: { color: 'warning', icon: <Warning /> },
    'out-of-service': { color: 'error', icon: <Error /> }
  };

  const typeConfig = {
    heavy: { icon: <Construction />, label: 'Heavy Machinery' },
    vehicle: { icon: <Engineering />, label: 'Vehicle' },
    hand: { icon: <Handyman />, label: 'Hand Tool' }
  };

  const handleAddEquipment = (newEquipment) => {
    setEquipment([...equipment, { ...newEquipment, id: equipment.length + 1 }]);
    setOpenDialog(false);
  };

  const getMaintenanceProgress = (equip) => {
    const last = new Date(equip.lastMaintenance);
    const next = new Date(equip.nextMaintenance);
    const total = next - last;
    const current = Date.now() - last;
    return Math.min((current / total) * 100, 100);
  };

  const handleDeleteEquipment = (id) => {
    setEquipment(prev => prev.filter(e => e.id !== id));
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <div className="flex items-center justify-between mb-6">
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          <Engineering sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Equipment Management
        </Typography>

        <div className="flex gap-4">
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Filter by Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="operational">Operational</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="out-of-service">Out of Service</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
          >
            Add Equipment
          </Button>
        </div>
      </div>

      <Grid container spacing={3}>
        {equipment
          .filter(e => filterStatus === 'all' || e.status === filterStatus)
          .map((equip, index) => (
            <Grid item xs={12} sm={6} lg={4} key={equip.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Paper sx={{
                  p: 3,
                  height: '100%',
                  borderLeft: `4px solid ${theme.palette[statusConfig[equip.status].color].main}`,
                  position: 'relative'
                }}>
                  <div className="flex items-center justify-between mb-3">
                    <Chip
                      label={typeConfig[equip.type].label}
                      icon={typeConfig[equip.type].icon}
                      variant="outlined"
                    />
                    <div className="flex gap-2">
                      <IconButton size="small">
                        
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteEquipment(equip.id)}
                      >
                        <Delete />
                      </IconButton>
                    </div>
                  </div>

                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {equip.name}
                  </Typography>

                  <div className="flex items-center gap-2 mb-2">
                    {statusConfig[equip.status].icon}
                    <Typography variant="body2" color="textSecondary">
                      {equip.status.replace('-', ' ').toUpperCase()}
                    </Typography>
                  </div>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <CalendarToday fontSize="small" sx={{ mr: 1 }} />
                    Next Maintenance: {new Date(equip.nextMaintenance).toLocaleDateString()}
                  </Typography>

                  <LinearProgress
                    variant="determinate"
                    value={getMaintenanceProgress(equip)}
                    color={statusConfig[equip.status].color}
                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                  />

                  <div className="flex items-center justify-between">
                    <Typography variant="caption" color="textSecondary">
                      Location: {equip.location}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Hours: {equip.hoursUsed}
                    </Typography>
                  </div>
                </Paper>
              </motion.div>
            </Grid>
          ))}
      </Grid>

      <AddEquipmentDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        onSave={handleAddEquipment}
      />
    </Paper>
  );
};

const AddEquipmentDialog = ({ open, onClose, onSave }) => {
  const [newEquipment, setNewEquipment] = useState({
    name: '',
    type: 'heavy',
    status: 'operational',
    location: ''
  });

  const handleSave = () => {
    onSave(newEquipment);
    setNewEquipment({ name: '', type: 'heavy', status: 'operational', location: '' });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Add New Equipment</DialogTitle>
      <DialogContent sx={{ minWidth: 400 }}>
        <TextField
          autoFocus
          margin="dense"
          label="Equipment Name"
          fullWidth
          value={newEquipment.name}
          onChange={(e) => setNewEquipment({ ...newEquipment, name: e.target.value })}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel>Equipment Type</InputLabel>
          <Select
            value={newEquipment.type}
            onChange={(e) => setNewEquipment({ ...newEquipment, type: e.target.value })}
          >
            <MenuItem value="heavy">Heavy Machinery</MenuItem>
            <MenuItem value="vehicle">Vehicle</MenuItem>
            <MenuItem value="hand">Hand Tool</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense">
          <InputLabel>Initial Status</InputLabel>
          <Select
            value={newEquipment.status}
            onChange={(e) => setNewEquipment({ ...newEquipment, status: e.target.value })}
          >
            <MenuItem value="operational">Operational</MenuItem>
            <MenuItem value="maintenance">Maintenance</MenuItem>
            <MenuItem value="out-of-service">Out of Service</MenuItem>
          </Select>
        </FormControl>
        <TextField
          margin="dense"
          label="Location"
          fullWidth
          value={newEquipment.location}
          onChange={(e) => setNewEquipment({ ...newEquipment, location: e.target.value })}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default Equipment;